<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import dayjs from 'dayjs'

import { DashboardWidget, WidgetType } from '@/types/dashboard'
import { useAuthStore } from '@/stores/auth-store'
import { useDashboardStore } from '@/stores/dashboard-store'
import { image } from '@/utils/assets'
import { MinimalResource, uuid } from '@/types/general'
import { DropdownStringOption } from '@/types/inputs'
import { useLazyLoadedList } from '@/hooks/use-lazy-loaded-list'
import { formatNumber, newMoneyFormatter } from '@/utils/numbers'
import { useCalculatedRowHeight } from '@/hooks/use-calculated-row-height'

import TimePeriodOption from '@/components/dashboard/widget-settings/TimePeriodOption.vue'
import CompanyLocationOption from '@/components/dashboard/widget-settings/CompanyLocationOption.vue'
import MyWidget from '@/components/dashboard/widgets/MyWidget.vue'
import MyInput from '@/components/my-components/form/MyInput.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import MySelect from '@/components/my-components/form/MySelect.vue'
import MyPanel from '@/components/my-components/MyPanel.vue'
import MyCheckbox from '@/components/my-components/form/MyCheckbox.vue'

export interface Props {
    widget: DashboardWidget<WidgetType.PackagingIncomingOutgoingStats>
    preview?: boolean
}

interface ProductBalance extends BalanceRows {
    productId: uuid
    productName: string
    productImage: uuid
}

interface Balance {
    amount: number
    price: number
}

interface BalanceRows {
    sent: Balance
    deliveredAtCustomers: Balance
    collectedAtCustomers: Balance
    received: Balance
    total: Balance
}

interface BalanceResponse {
    productBalances: ProductBalance[]
    totalBalances: BalanceRows
    currency: string
    mainCompanyName: string
    shipperCompanyName: string
}

const props = defineProps<Props>()

const balanceTypes: (keyof BalanceRows)[] = [
    'sent',
    'deliveredAtCustomers',
    'collectedAtCustomers',
    'received',
    'total',
]

const tableElement = ref<HTMLElement>()

const { t } = useI18n()
const authStore = useAuthStore()
const dashboardStore = useDashboardStore()
const customerCompanies = useLazyLoadedList<DropdownStringOption>(fetchCustomers)
const calculatedRowHeight = useCalculatedRowHeight(tableElement, {
    rowCount: 5,
    headerHeight: 102,
})

const loading = ref(false)
const balances = ref<BalanceResponse>({
    productBalances: [],
    totalBalances: {
        sent: { amount: 0, price: 0 },
        received: { amount: 0, price: 0 },
        deliveredAtCustomers: { amount: 0, price: 0 },
        collectedAtCustomers: { amount: 0, price: 0 },
        total: { amount: 0, price: 0 },
    },
    currency: 'USD',
    mainCompanyName: '',
    shipperCompanyName: '',
})

const timePeriod = computed({
    get: () => props.widget.options.timePeriod ?? 7,
    set: (timePeriod) => dashboardStore.updateWidgetOptions(props.widget, { timePeriod }),
})

const title = computed({
    get: () => props.widget.options.title ?? '',
    set: (title) => dashboardStore.updateWidgetOptions(props.widget, { title }),
})

const customerCompanyId = computed({
    get: () => props.widget.options.customerCompanyId ?? null,
    set: (customerCompanyId) =>
        dashboardStore.updateWidgetOptions(props.widget, { customerCompanyId }),
})

const customerLocationId = computed({
    get: () => props.widget.options.customerLocationId ?? null,
    set: (id) => {
        dashboardStore.updateWidgetOptions(props.widget, { customerLocationId: id })
    },
})

const locationId = computed({
    get: () => props.widget.options.locationId ?? null,
    set: (id) => {
        dashboardStore.updateWidgetOptions(props.widget, { locationId: id })
    },
})

const viewAsShipper = computed({
    get: () => props.widget.options.viewAsShipper ?? false,
    set: (viewAsShipper) => dashboardStore.updateWidgetOptions(props.widget, { viewAsShipper }),
})

const moneyFormatter = computed(() => {
    return newMoneyFormatter(balances.value?.currency || 'USD')
})

async function fetchBalance() {
    if (!customerCompanyId.value) return
    if (loading.value) return
    loading.value = true

    const params = {
        dashboard: dashboardStore.currentDashboard!.id,
        shareToken: !authStore.user ? dashboardStore.currentDashboard!.shareToken : null,
        'start-date': dayjs().subtract(timePeriod.value, 'days').toISOString(),
        'customer-company-id': customerCompanyId.value,
        'customer-location-id': customerLocationId.value,
        'location-id': locationId.value,
        'view-as-shipper': viewAsShipper.value,
    }
    const response = await axios.get<BalanceResponse>(
        window.route('dashboards.packaging-incoming-outgoing-stats', params),
    )

    balances.value = response.data
    loading.value = false
}

async function fetchCustomers(): Promise<DropdownStringOption[]> {
    const response = await axios.get<MinimalResource[]>(
        window.route('minimal.companies.customers', {
            company: dashboardStore.currentDashboard?.companyId,
            'without-new': true,
        }),
    )
    return response.data.map((company) => {
        return {
            label: company.name,
            value: company.id,
        }
    })
}

function populateCustomers() {
    if (!customerCompanyId.value) return
    customerCompanies.fetch()
}

onMounted(() => fetchBalance())

watch([timePeriod, customerCompanyId, customerLocationId, locationId], fetchBalance)

watch(
    () => viewAsShipper.value,
    () => {
        locationId.value = null
        fetchBalance()
    },
)
</script>

<template>
    <MyWidget :widget="props.widget" :preview="props.preview" @settings-opened="populateCustomers">
        <LoaderWrapper v-if="loading" :visible="loading" />
        <div ref="tableElement" class="overflow-x-auto h-full">
            <table class="w-full min-w-full table-fixed h-full">
                <thead class="h-[102px]">
                    <tr class="border-collapse">
                        <td
                            class="text-slate-400 dark:text-slate-200 sticky left-0 w-60 bg-primary-50 text-start px-6 align-middle text-xl font-semibold shadow-lg drop-shadow-lg dark:bg-dark-500 dark:shadow-black"
                        >
                            {{ title }}
                        </td>

                        <td
                            class="text-slate-400 dark:text-slate-200 w-24 py-2 px-4 text-center align-bottom text-xs font-medium uppercase h-full flex-col items-center justify-center"
                            v-text="t('total')"
                        />
                        <td
                            v-for="product in balances.productBalances"
                            :key="product.productId"
                            class="text-slate-400 dark:text-slate-200 w-40 py-2 font-medium odd:bg-black/5 dark:border-dark-600 dark:odd:bg-black/20 rounded-t-xl"
                        >
                            <div class="flex h-full flex-col items-center justify-center">
                                <img
                                    class="h-[60px] object-contain"
                                    :src="image(product.productImage)"
                                    :alt="product.productName"
                                />

                                <div
                                    class="mt-2 flex items-center justify-between text-center text-xs uppercase"
                                    v-text="product.productName"
                                />
                            </div>
                        </td>
                    </tr>
                </thead>

                <tbody>
                    <tr
                        v-for="balanceType in balanceTypes"
                        :key="balanceType"
                        class="group odd:bg-primary-100 hover:bg-primary-300 dark:odd:bg-dark-400 dark:hover:bg-dark-700"
                    >
                        <td
                            v-truncate-tooltip
                            class="sticky left-0 truncate bg-primary-50 px-3 shadow-lg drop-shadow-lg group-odd:bg-primary-100 group-hover:bg-primary-300 dark:bg-dark-500 dark:group-odd:bg-dark-400 dark:group-hover:bg-dark-700 font-semibold text-primary-400 dark:text-white"
                        >
                            <template v-if="balanceType === 'sent'">
                                {{ t('sentByCompany', { company: balances.mainCompanyName }) }}
                            </template>

                            <template v-else-if="balanceType === 'deliveredAtCustomers'">
                                {{
                                    t('deliveredAtCustomersByCompany', {
                                        company: balances.shipperCompanyName,
                                    })
                                }}
                            </template>

                            <template v-else-if="balanceType === 'collectedAtCustomers'">
                                {{
                                    t('collectedAtCustomersByCompany', {
                                        company: balances.shipperCompanyName,
                                    })
                                }}
                            </template>

                            <template v-else-if="balanceType === 'received'">
                                {{ t('receivedByCompany', { company: balances.mainCompanyName }) }}
                            </template>

                            <template v-else>{{ t('total') }}</template>
                        </td>
                        <td
                            class="text-slate-400 dark:text-slate-200 w-40 py-2 font-medium odd:bg-black/5 dark:border-dark-600 dark:odd:bg-black/20"
                            :style="{ height: calculatedRowHeight + 'px' }"
                        >
                            <div class="flex h-full flex-col items-center justify-center">
                                <span>
                                    {{ formatNumber(balances.totalBalances[balanceType].amount) }}
                                </span>

                                {{
                                    moneyFormatter.format(balances.totalBalances[balanceType].price)
                                }}
                            </div>
                        </td>
                        <td
                            v-for="product in balances.productBalances"
                            :key="product.productId"
                            class="text-slate-400 dark:text-slate-200 w-40 py-2 font-medium odd:bg-black/5 dark:border-dark-600 dark:odd:bg-black/20"
                        >
                            <div class="flex h-full flex-col items-center justify-center">
                                <span v-text="formatNumber(product[balanceType].amount)" />
                                <span v-text="moneyFormatter.format(product[balanceType].price)" />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <template #settings>
            <MyInput v-model="title" group-class="mt-4 mx-auto" :label="t('title')" />

            <TimePeriodOption v-model="timePeriod" />

            <MyPanel
                background="dark:bg-dark-600 bg-primary-200"
                padded
                class="pt-0"
                panel-class="mt-4"
                :invisible="!customerCompanyId"
            >
                <div class="flex space-x-2 mt-4">
                    <MyCheckbox v-model="viewAsShipper" :label="t('viewAsShipper')" />
                    <mdi:information v-tooltip="t('viewAsShipperExplanation')" />
                </div>

                <CompanyLocationOption v-if="viewAsShipper" v-model="locationId" />

                <MySelect
                    v-model="customerCompanyId"
                    :options="customerCompanies.items.value"
                    :label="t('customer')"
                    searchable
                    group-class="mt-4 mx-auto"
                    @focus="customerCompanies.fetch()"
                />

                <CompanyLocationOption
                    v-if="customerCompanyId"
                    v-model="customerLocationId"
                    :customer-company="customerCompanyId ?? undefined"
                />
            </MyPanel>
        </template>
    </MyWidget>
</template>
